// RedeemPanel — the redeem DIALOG (account-area redesign Task 10; the
// 2026-10-02 amendment F18-F20/O7).
//
// ⚠️ REWRITTEN 2026-10-04 (redesign Task 10). ORIGINAL (TD-384 T26): an
// always-mounted sub-tab body — a "New Request" form (processor-MID select,
// Amount (USD), the disclosure gate) above the "Your Requests" card list.
// CURRENT: the form is the dialog RedemptionsPanel opens from its primary
// "Redeem credits" control; the request history moved to that panel's merged
// table (RedemptionsPanel.tsx). The MID select is REPLACED by the
// store-first account resolution the amendment pins:
//   1. the caller picks a STORE (GET /me/referral-redemption-stores);
//   2. a read-only "Merchant service account" field shows that store's
//      ACTIVE account (GET /me/mids where store_id matches — pending/closed
//      accounts never resolve);
//   3. none → the amendment's EXACT message, the submit disabled, and a link
//      routing to Merchant services;
//   4. several → a second select of ONLY that store's accounts.
// ⚠️ AMENDED 2026-10-10 (merge-review M2, TD-586). ORIGINAL: the store list
// came from GET /me/stores, so a 403 there (a member whose role lacks
// manage-stores -- the O7 ACL gap) showed the amendment's manager message and
// dead-ended a member who CAN redeem credits. CURRENT: the list comes from
// GET /me/referral-redemption-stores, which sits under participate-referrals
// (operator answer 2026-10-10, "Redeem-only store list (Recommended)": "A new
// endpoint under the redeem permission returns just the account's stores (name
// and whether a merchant account is linked), enough to pick one. It grants no
// store editing."). The manager message now answers only a 403 on THAT route,
// and TD-586's second half is closed: a stores list that SUCCEEDS but is EMPTY
// renders REDEEM_NO_STORES_MESSAGE instead of a silently disabled submit. The
// per-store `has_active_merchant_account` flag is authoritative for the
// no-account message (it needs no MID read); the active account itself -- the
// id the redemption POST needs -- still comes from GET /me/mids.
// ⚠️ AMENDED 2026-10-10 (merge-review M2 fix round 1, TD-605). ORIGINAL: the
// account the POST needs came ONLY from GET /me/mids (manage-mids), so a member
// who could redeem but not read accounts picked a store and then met
// REDEEM_MIDS_FORBIDDEN_MESSAGE -- a dead end even for the usual single-account
// store. CURRENT (operator ruling, answer "1a", verbatim: "Send the store. If it
// has exactly one active account, the server uses it. If it has several, the
// server replies "choose an account", and only members who can see merchant
// accounts can pick one. Single-account stores, the usual case, need only the
// redeem permission."): a member WITHOUT manage-mids never calls GET /me/mids
// and POSTs { store_id }; a manage-mids member keeps today's behaviour (the
// second select for several accounts, POST { mid_id }). A 403 on GET /me/mids
// (a stale or failed self-view) degrades to the same store_id mode. A store with
// several accounts answers 422 ACCOUNT_CHOICE_REQUIRED, shown as
// REDEEM_CHOICE_REQUIRED_MESSAGE. The POST body is now { mid_id | store_id,
// requested_amount, disclosure_id } -- exactly one of the first two.
//
// THE DISCLOSURE GATE IS BYTE-IDENTICAL to the ORIGINAL (the amendment's
// kept set — §4.4.4's load-bearing control; the checkbox label, the shown
// body verbatim, the disabled-until-accepted submit, the handler's own
// acceptance guard — a disabled button does not stop a direct form submit —
// the POST carrying disclosure_id, and the 409 DISCLOSURE_STALE reset that
// re-reads the active revision and clears the acceptance). The ONLY allowed
// change is the submit label's casing ("Request Redemption" → "Request
// redemption"). The POST body is unchanged: { mid_id, requested_amount,
// disclosure_id } — the server stays the authority on every rule the client
// mirrors (its 422 is the control for the amount cap too).
import { useCallback, useEffect, useState } from "react";
import {
  EmbedBanner,
  EmbedButton,
  EmbedCheckbox,
  EmbedDialog,
  EmbedField,
  EmbedInput,
  EmbedLink,
  EmbedSelect,
  EmbedText,
  useEmbedTokens,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import type { MerchantMIDWire } from "./mid-api";
import { listMyMIDs } from "./mid-api";
import {
  getActiveReferralDisclosure,
  listRedeemStores,
  openMyRedemption,
  type ActiveReferralDisclosureWire,
  type RedeemStoreWire,
  type ReferralBalanceWire,
  type ReferralRedemptionWire,
} from "./referral-api";
import type { SectionId } from "./account-sections";

/** TD-586 second half: the redeem-only store list SUCCEEDED but is empty. A
 *  distinct sentence from the 403 wording -- "no stores" is not "not allowed". */
export const REDEEM_NO_STORES_MESSAGE =
  "Your account has no stores yet. Add a store and link it to a merchant account, then come back to redeem your credits.";

/** TD-605: the server answered 422 ACCOUNT_CHOICE_REQUIRED -- the chosen store
 *  holds more than one merchant account and this member cannot see accounts to
 *  pick one. DRAFT COPY, listed for operator review. */
export const REDEEM_CHOICE_REQUIRED_MESSAGE =
  "This store has more than one merchant account. Someone with merchant-account access on your account needs to submit this redemption request.";

const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;

function isValidAmount(raw: string, max: number | null): string | null {
  if (!AMOUNT_PATTERN.test(raw)) {
    return "Enter an amount greater than zero, with at most two decimal places.";
  }
  const value = Number(raw);
  if (!(value > 0)) {
    return "Enter an amount greater than zero, with at most two decimal places.";
  }
  // The client refusal is a courtesy mirror of the server's 422 (which stays
  // the control): max = the spendable balance, formatted from the wire's
  // decimal string so "$350.00" matches the summary figure exactly.
  if (max !== null && value > max) {
    return `You can redeem up to $${max.toFixed(2)}`;
  }
  return null;
}

export interface RedeemDialogProps {
  open: boolean;
  onClose: () => void;
  /** The credit balance from the host panel — the client-side amount cap
   *  reads its spendable figure. Null/malformed skips the client cap (the
   *  server's 422 remains the control). */
  balance: ReferralBalanceWire | null;
  /** A created request — the host prepends it and re-reads the balance. */
  onOpened: (row: ReferralRedemptionWire) => void;
  /** The "Link a merchant account in Merchant services" escape hatch. */
  onNavigateSection?: (id: SectionId) => void;
  /** TD-605: whether the member holds manage-mids (the self-view's answer).
   *  False -> GET /me/mids is never called and the request carries store_id.
   *  Default true keeps standalone mounts on today's behaviour (a 403 on the
   *  read still degrades to store_id mode). */
  canViewAccounts?: boolean;
}

export function RedeemDialog({
  open,
  onClose,
  balance,
  onOpened,
  onNavigateSection,
  canViewAccounts = true,
}: RedeemDialogProps) {
  const t = useEmbedTokens();

  const [stores, setStores] = useState<RedeemStoreWire[]>([]);
  const [storesLoaded, setStoresLoaded] = useState(false);
  const [storesError, setStoresError] = useState<string | null>(null);
  const [storeId, setStoreId] = useState<string | null>(null);

  const [mids, setMids] = useState<MerchantMIDWire[]>([]);
  const [loadingMids, setLoadingMids] = useState(true);
  const [midsError, setMidsError] = useState<string | null>(null);
  // A 403 on GET /me/mids is an access refusal, not an outage: no Retry, and
  // the request switches to store_id mode (TD-605) instead of dead-ending.
  const [midsForbidden, setMidsForbidden] = useState(false);

  const [disclosure, setDisclosure] = useState<ActiveReferralDisclosureWire | null>(null);
  const [disclosureUnavailable, setDisclosureUnavailable] = useState(false);
  const [disclosureError, setDisclosureError] = useState<string | null>(null);

  const [accountId, setAccountId] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [amountError, setAmountError] = useState<string | null>(null);

  const messageOf = useCallback(
    (reason: unknown, fallback: string): string => (reason instanceof ApiError ? reason.message : fallback),
    [],
  );

  // Fetches live behind `open` — EmbedDialog renders null while closed, so a
  // closed dialog must not fire requests (the account area mounts this once
  // per section, not per open).
  const loadStores = useCallback(async () => {
    setStoresError(null);
    setStoresLoaded(false);
    try {
      const data = await listRedeemStores();
      const list = Array.isArray(data) ? data : [];
      setStores(list);
      setStoreId(list[0]?.id ?? null);
    } catch (err) {
      setStores([]);
      setStoreId(null);
      // The 403 ACL refusal (a role lacking participate-referrals) renders
      // the amendment's exact manager message; any other failure renders its
      // own.
      setStoresError(
        err instanceof ApiError && err.status === 403
          ? "Ask an account manager to link a store to a merchant account"
          : messageOf(err, "Failed to load your stores"),
      );
    } finally {
      setStoresLoaded(true);
    }
  }, [messageOf]);

  const loadMids = useCallback(async () => {
    setLoadingMids(true);
    setMidsError(null);
    setMidsForbidden(false);
    try {
      const data = await listMyMIDs();
      setMids(Array.isArray(data) ? data : []);
    } catch (err) {
      setMids([]);
      setMidsForbidden(err instanceof ApiError && err.status === 403);
      setMidsError(messageOf(err, "Failed to load your merchant accounts"));
    } finally {
      setLoadingMids(false);
    }
  }, [messageOf]);

  const loadDisclosure = useCallback(async () => {
    setDisclosureError(null);
    setDisclosureUnavailable(false);
    try {
      setDisclosure(await getActiveReferralDisclosure("redemption_request"));
    } catch (err) {
      setDisclosure(null);
      if (err instanceof ApiError && err.status === 404) {
        // Refuse, never fall back: no active disclosure is the configured
        // program's "off" state, not a failure.
        setDisclosureUnavailable(true);
      } else {
        setDisclosureError(messageOf(err, "Failed to load the redemption disclosure"));
      }
    }
  }, [messageOf]);

  useEffect(() => {
    if (!open) return;
    setAmount("");
    setAccepted(false);
    setFormError(null);
    setAmountError(null);
    loadStores();
    if (canViewAccounts) {
      loadMids();
    } else {
      // TD-605: no manage-mids -> the accounts read is NEVER sent.
      setMids([]);
      setMidsError(null);
      setMidsForbidden(false);
      setLoadingMids(false);
    }
    loadDisclosure();
  }, [open, canViewAccounts, loadStores, loadMids, loadDisclosure]);

  // The store's ACTIVE accounts only (§ amendment: pending/closed never
  // resolve — a pending verification is not a redeemable account).
  const storeAccounts = mids.filter((m) => m.store_id === storeId && m.status === "active");
  // The server's per-store flag (has_active_merchant_account === false) is
  // AUTHORITATIVE for "no account": it needs no MID read, so it holds even
  // while the mids list is loading or refused. It also vetoes a resolved
  // account below, so the message and the submit button can never disagree.
  const selectedStore = stores.find((st) => st.id === storeId);
  const flagNoAccount = selectedStore?.has_active_merchant_account === false;
  // Several → the second select defaults to the FIRST (the server's
  // list order created_at ASC, id ASC is deterministic). Kept in sync until
  // the merchant picks one; a store switch re-resolves.
  const resolvedAccountId = flagNoAccount
    ? null
    : storeAccounts.some((m) => m.id === accountId)
      ? accountId
      : storeAccounts[0]?.id ?? null;

  // TD-605: the request carries store_id (the server resolves the account) when
  // this member cannot read accounts, by role or because the read was refused.
  const storeMode = !canViewAccounts || midsForbidden;
  const canSubmitTarget = storeMode ? selectedStore?.has_active_merchant_account === true : !!resolvedAccountId;

  const spendableMax =
    balance && typeof balance.spendable === "string" && Number.isFinite(Number(balance.spendable))
      ? Number(balance.spendable)
      : null;

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      // Arm 2 of the disclosure gate: a DIRECT form submit (Enter key,
      // programmatic) bypasses a disabled button — the acceptance guard
      // lives HERE, not only in the button state. Byte-identical to the
      // ORIGINAL panel's handler contract.
      if (!accepted || !disclosure) return;
      setFormError(null);
      if (!canSubmitTarget) {
        setFormError("Choose the merchant account to redeem against.");
        return;
      }
      const amountProblem = isValidAmount(amount.trim(), spendableMax);
      setAmountError(amountProblem);
      if (amountProblem) return;
      setSubmitting(true);
      try {
        const row = await openMyRedemption({
          ...(storeMode ? { store_id: storeId! } : { mid_id: resolvedAccountId! }),
          requested_amount: amount.trim(),
          disclosure_id: disclosure.id,
        });
        onOpened(row);
        onClose();
      } catch (err) {
        if (err instanceof ApiError && err.code === "ACCOUNT_CHOICE_REQUIRED") {
          setFormError(REDEEM_CHOICE_REQUIRED_MESSAGE);
        } else if (err instanceof ApiError && err.code === "DISCLOSURE_STALE") {
          // The accepted revision is no longer active: re-read it and RESET
          // the acceptance — the merchant must re-accept the current terms
          // (byte-identical ORIGINAL behavior).
          setAccepted(false);
          setFormError(err.message);
          await loadDisclosure();
        } else {
          setFormError(messageOf(err, "Failed to submit this request. Please try again."));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [accepted, disclosure, canSubmitTarget, storeMode, storeId, resolvedAccountId, amount, spendableMax, onOpened, onClose, loadDisclosure, messageOf],
  );


  // The no-account message + the Merchant services escape hatch. Rendered for
  // BOTH conclusions: the server flag says false (flagNoAccount), or the flag
  // says true but the settled mids read resolves no ACTIVE account for the
  // store. One element, so the two paths cannot drift (the amendment's exact
  // string is pinned by the tests).
  const noAccountBlock = (
    <div style={{ marginBottom: t.spacing.md }}>
      <div data-testid="redeem-no-account" style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary }}>
        Store does not have merchant account associated
      </div>
      {onNavigateSection && (
        <div style={{ marginTop: t.spacing.sm }}>
          <EmbedLink
            type="button"
            onClick={() => {
              onClose();
              onNavigateSection("merchant-service");
            }}
          >
            Link a merchant account in Merchant services
          </EmbedLink>
        </div>
      )}
    </div>
  );

  return (
    <EmbedDialog open={open} onClose={onClose} title="Redeem credits" size="medium">
      {disclosureError ? (
        <div>
          <EmbedBanner tone="error">{disclosureError}</EmbedBanner>
          <div style={{ marginTop: t.spacing.md }}>
            <EmbedButton type="button" variant="secondary" onClick={loadDisclosure}>
              Retry
            </EmbedButton>
          </div>
        </div>
      ) : disclosureUnavailable ? (
        <div>
          {/* None active is the configured-program's "off" state, not a
              failure — the ORIGINAL panel withheld the form the same way. */}
          <EmbedText tone="secondary">
            Redemption requests are not available yet. Please check back soon.
          </EmbedText>
        </div>
      ) : storesError ? (
        // No stores → nothing to resolve an account against: the form is
        // withheld entirely (the server's area check stays the control; this
        // only explains it).
        <EmbedBanner tone="error">{storesError}</EmbedBanner>
      ) : storesLoaded && stores.length === 0 ? (
        // TD-586, second half: the list SUCCEEDED and is empty. Say so -- the
        // form is withheld rather than shown with a submit that can never
        // enable and no reason why.
        <p role="status" data-testid="redeem-no-stores" style={{ margin: 0 }}>
          <EmbedText tone="secondary">{REDEEM_NO_STORES_MESSAGE}</EmbedText>
        </p>
      ) : !disclosure ? (
        <p role="status" style={{ margin: 0 }}>
          <EmbedText tone="secondary">Loading...</EmbedText>
        </p>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <div style={{ marginBottom: t.spacing.md, border: `1px solid ${t.colors.borderSubtle}`, borderRadius: t.borderRadius, padding: t.spacing.md }}>
            <div data-testid="redeem-disclosure-body" style={{ whiteSpace: "pre-wrap", fontSize: t.fontSize.sm, lineHeight: t.lineHeight.normal, color: t.colors.text }}>
              {disclosure.body}
            </div>
            <div style={{ marginTop: t.spacing.sm, fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>
              Effective {disclosure.effective_from.slice(0, 10)} · revision {disclosure.revision}
            </div>
          </div>

          {/* The store is chosen FIRST (the amendment); a merchant-account
              load failure degrades only the ACCOUNT resolution below to an
              error + Retry — the store select stays usable and submit stays
              disabled without a resolved account. */}
          <EmbedField label="Store" htmlFor="redeem-store" required>
            <EmbedSelect
              id="redeem-store"
              value={storeId ?? ""}
              onChange={(e) => {
                setStoreId(e.target.value || null);
                setAccountId(null);
              }}
            >
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </EmbedSelect>
          </EmbedField>

          {flagNoAccount ? (
            noAccountBlock
          ) : storeMode ? null : midsError ? (
            <div style={{ marginBottom: t.spacing.md }}>
              <EmbedBanner tone="error">{midsError}</EmbedBanner>
              <div style={{ marginTop: t.spacing.md }}>
                <EmbedButton type="button" variant="secondary" onClick={loadMids}>
                  Retry
                </EmbedButton>
              </div>
            </div>
          ) : loadingMids ? (
            // The empty-set conclusion below is drawn only on SETTLED data:
            // while the mids GET is in flight a store that HAS an active
            // account must not be told it has none.
            <p role="status" style={{ margin: 0, marginBottom: t.spacing.md }}>
              <EmbedText tone="secondary">Loading your merchant accounts...</EmbedText>
            </p>
          ) : resolvedAccountId ? (
            storeAccounts.length > 1 ? (
              <EmbedField label="Merchant service account" htmlFor="redeem-account">
                <EmbedSelect
                  id="redeem-account"
                  value={resolvedAccountId}
                  onChange={(e) => setAccountId(e.target.value)}
                >
                  {storeAccounts.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.mid_number}
                    </option>
                  ))}
                </EmbedSelect>
              </EmbedField>
            ) : (
              // The read-only resolution: the store's one ACTIVE account
              // — shown, not chosen (§66: a filterable picker shows the
              // label; here there is nothing to filter).
              <EmbedField label="Merchant service account" htmlFor="redeem-account-number">
                <span
                  id="redeem-account-number"
                  data-testid="redeem-account-number"
                  style={{ fontSize: t.fontSize.sm, color: t.colors.text, overflowWrap: "anywhere" }}
                >
                  {storeAccounts[0]!.mid_number}
                </span>
              </EmbedField>
            )
          ) : storeId !== null ? (
            noAccountBlock
          ) : null}

          <EmbedField label="Amount" htmlFor="redeem-amount" required error={amountError ?? undefined}>
            <EmbedInput
              id="redeem-amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => {
                const next = e.target.value;
                setAmount(next);
                // Validate as the merchant types: the cap refusal (and the
                // shape refusal) appear WITHOUT a submit round — the client
                // refuses FIRST, before any network call (the server's 422
                // stays the control). Empty input never nags.
                setAmountError(next === "" ? null : isValidAmount(next, spendableMax));
              }}
            />
          </EmbedField>

          <div style={{ marginTop: t.spacing.md }}>
            <EmbedCheckbox
              id="redeem-accept"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              label={<EmbedText size="xs">I have read and agree to the redemption disclosure above.</EmbedText>}
            />
          </div>

          {formError && (
            <div style={{ marginTop: t.spacing.md }}>
              <EmbedBanner tone="error">{formError}</EmbedBanner>
            </div>
          )}

          <div style={{ marginTop: t.spacing.lg, display: "flex", gap: t.spacing.sm, justifyContent: "flex-end" }}>
            <EmbedButton type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </EmbedButton>
            {/* Arm 1 of the disclosure gate: disabled until accepted (and
                now also until the store resolves an account). The ONLY label
                change the amendment allowed: Title → sentence case. */}
            <EmbedButton type="submit" disabled={submitting || !accepted || !canSubmitTarget}>
              {submitting ? "Submitting..." : "Request redemption"}
            </EmbedButton>
          </div>
        </form>
      )}
    </EmbedDialog>
  );
}
